import React, { useState, useEffect, useContext, useRef, createContext } from 'react';
import { Search, Filter, ArrowUpDown, X, Eye, ArrowLeft, ChevronLeft, ChevronRight, Calendar, MessageCircle } from 'lucide-react';
import '../../../../assests/css/admin/OrdersPage/OrdersPage.css';
import AppContext from '../../../../context/AppContext';
import axios from 'axios';

const TABS      = ['All Order', 'Summary', 'Completed', 'Pending', 'Return', 'On the Way', 'Cancelled'];
const STATUSES  = ['Completed', 'Pending', 'On the Way', 'Return', 'Cancelled'];
const PAGE_SIZE = 6;

const fmtAmount = (n) => `Rs.${Number(n || 0).toFixed(2)}`;


const getOrderId   = (order) => 'ORD-' + String(order.id).padStart(4, '0');
const getDate      = (order) => (order.created_at || '').split('T')[0];
const getName      = (order) => `${order.firstName || ''} ${order.lastName || ''}`.trim();
const getItems     = (order) => Array.isArray(order.cartItems) ? order.cartItems.length : 0;
const getPayment   = (order) => order.paymentMethod || '';
const getAmount    = (order) => order.total || 0;

const StatusBadge = ({ status }) => {
  const map = {
    Completed:    'badge-completed',
    Pending:      'badge-pending',
    'On the Way': 'badge-on-the-way',
    Return:       'badge-return',
    Cancelled:    'badge-cancelled',
  };
  return <span className={`OP-badge-status ${map[status] || 'badge-pending'}`}>{status}</span>;
};

// ── Editable status dropdown (admin changes order status here) ──
// order.status might not exactly match STATUSES (e.g. "Processing", different casing)
// so we inject it as an extra option — otherwise <select> silently falls back to the
// first option and every row looks "Completed" even when it isn't.
const StatusSelect = ({ order, updating, onChange }) => {
  const options = STATUSES.includes(order.status) ? STATUSES : [order.status, ...STATUSES];
  return (
    <div className="OP-status-select-wrapper">
      <select
        className="OP-status-select"
        value={order.status}
        disabled={updating}
        onChange={(e) => onChange(order, e.target.value)}
      >
        {options.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    </div>
  );
};

const SweetAlert = ({ message, type = 'success' }) => (
  <div className={`OP-sweet-alert OP-sweet-alert-${type}`}>
    <div className="OP-alert-icon">{type === 'success' ? '✓' : type === 'danger' ? '✕' : '!'}</div>
    <div className="OP-alert-message">{message}</div>
  </div>
);

const OrderDetailView = ({ order, onBack, onStatusChange, updatingId }) => (
  <div className="OP-container">
    <div className="OP-header">
      <button className="OP-back-btn" onClick={onBack}>
        <ArrowLeft size={18} /> Back to Orders
      </button>
      <h1 className="OP-title">Order Details</h1>
      <p className="OP-subtitle">Full information for order {getOrderId(order)}</p>
    </div>
    <div className="OP-simple-form-layout">
      <div className="OP-section">
        <div className="OP-section-header">
          <span className="OP-section-title">🧾 Order Information</span>
          <StatusSelect order={order} updating={updatingId === order.id} onChange={onStatusChange} />
        </div>
        {[
          ['Order ID',       getOrderId(order)],
          ['Date',           getDate(order)],
          ['Customer Name',  getName(order)],
          ['Items',          `${getItems(order)} Item${getItems(order) > 1 ? 's' : ''}`],
          ['Payment Method', getPayment(order)],
          ['Total Amount',   fmtAmount(getAmount(order))],
        ].map(([label, val]) => (
          <div className="OP-detail-row" key={label}>
            <span className="OP-detail-label">{label}</span>
            <span className="OP-detail-value">{val}</span>
          </div>
        ))}
      </div>
      <div className="OP-actions">
        <button className="OP-cancel-btn" onClick={onBack}>Close</button>
      </div>
    </div>
  </div>
);

// ── Order Chat View (admin ↔ customer, per order) ──
const OrderChatView = ({ baseUrl, baseUrlForFiles, order, onBack }) => {
  const [messages,       setMessages]       = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [message,        setMessage]        = useState('');
  const [selectedImage,  setSelectedImage]  = useState(null);
  const [imagePreview,   setImagePreview]   = useState(null);
  const fileInputRef = useRef(null);

  const admin_id = localStorage.getItem('admin_id');

  const fetchConversation = () => {
    if (!baseUrl) return;
    axios
      .post(`${baseUrl}/admin/orders/fetchConversation`, {
        mlorder_id: order.id,
        admin_id:   admin_id,
      })
      .then((response) => {
        if (response.data.success) {
          const conversation = response.data.conversation;
          setConversationId(conversation.id);
          const newMessages = conversation.Mlchats || conversation.mlchats || [];
          setMessages((prev) => {
            if (prev.length === newMessages.length) return prev;
            return newMessages;
          });
        }
      })
      .catch((error) => {
        console.error('fetchConversation error:', error);
      });
  };

  useEffect(() => {
    fetchConversation();
    const interval = setInterval(fetchConversation, 3000);
    return () => clearInterval(interval);
  }, [order.id]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSendMessage = () => {
    if (!message.trim() && !selectedImage) return;
    if (!conversationId) return;

    const payload = new FormData();
    payload.append('Mlconversation_id', conversationId);
    payload.append('message', message);
    if (selectedImage) payload.append('image', selectedImage);

    axios
      .post(`${baseUrl}/admin/orders/sendMessage`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        if (response.data.success) {
          setMessages((prev) => [...prev, response.data.message]);
          setMessage('');
          handleRemoveImage();
        }
      })
      .catch((error) => {
        console.error('sendMessage error:', error);
      });
  };

  const getImageUrl = (path) => (path.startsWith('http') ? path : `${baseUrlForFiles}/${path}`);

  return (
    <div className="OP-container">
      <div className="OP-header">
        <button className="OP-back-btn" onClick={onBack}>
          <ArrowLeft size={18} /> Back to Orders
        </button>
        <h1 className="OP-title">Message — {getOrderId(order)}</h1>
        <p className="OP-subtitle">Chat with {getName(order) || 'customer'} about this order</p>
      </div>

      <div className="OP-chat-card">
        <div className="OP-chat-msg-list">
          {messages.length === 0 && (
            <p className="OP-chat-msg-empty">No messages yet.</p>
          )}
          {messages.map((msg, i) => (
            <div key={msg.id || i} className={`OP-chat-msg ${msg.sender === 'admin' ? 'OP-chat-msg--admin' : 'OP-chat-msg--customer'}`}>
              <div className="OP-chat-msg-header">
                <span className="OP-chat-msg-sender">
                  {msg.sender === 'admin' ? 'You (Admin)' : (getName(order) || 'Customer')}
                </span>
                <span className="OP-chat-msg-time">
                  {msg.created_at
                    ? new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                    : ''}
                </span>
              </div>
              {msg.image && (
                <img
                  src={getImageUrl(msg.image)}
                  alt="attachment"
                  className="OP-chat-msg-image"
                  onClick={() => window.open(getImageUrl(msg.image), '_blank')}
                />
              )}
              {msg.message && <div className="OP-chat-msg-text">{msg.message}</div>}
            </div>
          ))}
        </div>

        {imagePreview && (
          <div className="OP-chat-image-preview">
            <img src={imagePreview} alt="preview" />
            <button type="button" className="OP-chat-image-remove" onClick={handleRemoveImage}>×</button>
          </div>
        )}

        <div className="OP-chat-input-bar">
          <button className="OP-chat-attach-btn" onClick={() => fileInputRef.current?.click()}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16z" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </button>
          <input type="file" ref={fileInputRef} accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />
          <input
            type="text"
            className="OP-chat-msg-input"
            placeholder="Write a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
          />
          <button className="OP-chat-send-btn" onClick={handleSendMessage}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M18 2L9 11M18 2l-7 16-2-7-7-2 16-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const DateFilterModal = ({ onClose, onApply }) => {
  const [from, setFrom] = useState('');
  const [to,   setTo]   = useState('');

  return (
    <div className="OP-modal-overlay" onClick={onClose}>
      <div className="OP-modal" onClick={(e) => e.stopPropagation()}>
        <div className="OP-modal-header">
          <h3 className="OP-modal-title">Filter by Date</h3>
          <button className="OP-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="OP-form-group" style={{ marginTop: '1rem' }}>
          <label className="OP-label">From Date</label>
          <input type="date" className="OP-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="OP-form-group">
          <label className="OP-label">To Date</label>
          <input type="date" className="OP-input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="OP-modal-actions">
          <button className="OP-cancel-btn" onClick={() => { onApply(null, null); onClose(); }}>Reset</button>
          <button className="OP-save-btn" onClick={() => { onApply(from || null, to || null); onClose(); }}>Apply Filter</button>
        </div>
      </div>
    </div>
  );
};

const FiltersModal = ({ currentStatus, currentPayment, onClose, onApply }) => {
  const [status,  setStatus]  = useState(currentStatus  || 'All');
  const [payment, setPayment] = useState(currentPayment || 'All');
  const payments = ['All', 'Credit Card', 'PayPal', 'Apple Pay', 'Bank Transfer'];

  return (
    <div className="OP-modal-overlay" onClick={onClose}>
      <div className="OP-modal" onClick={(e) => e.stopPropagation()}>
        <div className="OP-modal-header">
          <h3 className="OP-modal-title">Advanced Filters</h3>
          <button className="OP-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="OP-form-group" style={{ marginTop: '1rem' }}>
          <label className="OP-label">Status</label>
          <div className="OP-status-options" style={{ marginTop: '0.4rem' }}>
            {['All', ...STATUSES].map((s) => (
              <button
                key={s}
                className={`OP-status-option-btn ${status === s ? 'OP-status-option-active' : ''}`}
                onClick={() => setStatus(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="OP-form-group">
          <label className="OP-label">Payment Method</label>
          <select className="OP-input" value={payment} onChange={(e) => setPayment(e.target.value)}>
            {payments.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="OP-modal-actions">
<button className="OP-cancel-btn" onClick={() => { setStatus('All'); setPayment('All'); onApply('All', 'All'); onClose(); }}>Reset</button>          <button className="OP-save-btn" onClick={() => { onApply(status, payment); onClose(); }}>Apply</button>
        </div>
      </div>
    </div>
  );
};

const RowActions = ({ order, onView, onMessage }) => (
  <div className="OP-row-actions">
    <button className="OP-icon-btn OP-icon-btn--view" title="View Details" onClick={() => onView(order)}>
      <Eye size={16} />
    </button>
    <button className="OP-icon-btn OP-icon-btn--message" title="Message Customer" onClick={() => onMessage(order)}>
      <MessageCircle size={16} />
    </button>
  </div>
);

const SummaryView = ({ orders }) => {
  const cards = [
    { label: 'Total Orders',  value: orders.length,                                                                                                        color: 'var(--primary-color)' },
    { label: 'Completed',     value: orders.filter((o) => o.status === 'Completed').length,                                                                color: '#16a34a' },
    { label: 'Pending',       value: orders.filter((o) => o.status === 'Pending').length,                                                                  color: '#b45309' },
    { label: 'On the Way',    value: orders.filter((o) => o.status === 'On the Way').length,                                                               color: '#1d4ed8' },
    { label: 'Returns',       value: orders.filter((o) => o.status === 'Return').length,                                                                   color: '#be185d' },
    { label: 'Cancelled',     value: orders.filter((o) => o.status === 'Cancelled').length,                                                                color: '#b91c1c' },
    { label: 'Total Revenue', value: fmtAmount(orders.filter((o) => o.status === 'Completed').reduce((a, o) => a + Number(o.total || 0), 0)),              color: 'var(--primary-color)' },
  ];
  return (
    <div className="OP-summary-grid">
      {cards.map((c) => (
        <div className="OP-summary-card" key={c.label}>
          <p className="OP-summary-label">{c.label}</p>
          <p className="OP-summary-value" style={{ color: c.color }}>{c.value}</p>
        </div>
      ))}
    </div>
  );
};

const OrdersPage = () => {
  const contextValue = useContext(AppContext);
  const baseUrl = contextValue?.baseUrl ?? '';
  const baseUrlForFiles = contextValue?.baseUrlForFiles ?? '';

  const [view, setView]                   = useState('list');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders,  setOrders]              = useState([]);
  const [loading, setLoading]             = useState(false);
  const [updatingId, setUpdatingId]       = useState(null);
  const [activeTab,     setActiveTab]     = useState('All Order');
  const [search,        setSearch]        = useState('');
  const [filterStatus,  setFilterStatus]  = useState('All');
  const [filterPayment, setFilterPayment] = useState('All');
  const [sortBy,        setSortBy]        = useState('date_desc');
  const [dateFrom,      setDateFrom]      = useState(null);
  const [dateTo,        setDateTo]        = useState(null);
  const [showSortMenu,     setShowSortMenu]     = useState(false);
  const [showDateModal,    setShowDateModal]    = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [page, setPage]                   = useState(1);
  const [showAlert,    setShowAlert]      = useState(false);
  const [alertMessage, setAlertMessage]   = useState('');
  const [alertType,    setAlertType]      = useState('success');

  useEffect(() => { fetchOrders(); }, []);
  useEffect(() => { setPage(1); }, [activeTab, search, filterStatus, filterPayment, dateFrom, dateTo]);

  const fetchOrders = () => {
    setLoading(true);
    axios.get(`${baseUrl}/admin/orders/getAllOrders`)
      .then((response) => {
        if (response.data.success && Array.isArray(response.data.data)) {
          setOrders(response.data.data);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch orders:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showSweetAlert = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  // ── Admin marks order status (e.g. Pending -> Completed) ──
  const handleStatusChange = (order, newStatus) => {
    if (newStatus === order.status) return;
    setUpdatingId(order.id);
    axios
      .post(`${baseUrl}/admin/orders/updateOrderStatus/${order.id}`, {
        status: newStatus,
      })
      .then((response) => {
        if (response.data.success) {
          setOrders((prev) =>
            prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o))
          );
          setSelectedOrder((prev) => (prev && prev.id === order.id ? { ...prev, status: newStatus } : prev));
          showSweetAlert(`Order ${getOrderId(order)} marked as ${newStatus}!`);
        } else {
          showSweetAlert(response.data.message || 'Failed to update status.', 'danger');
        }
      })
      .catch((error) => {
        console.error('updateStatus error:', error);
        showSweetAlert('Failed to update status.', 'danger');
      })
      .finally(() => {
        setUpdatingId(null);
      });
  };

  const filteredOrders = orders
    .filter((o) => {
      const tabMatch     = activeTab === 'All Order' || activeTab === 'Summary' || o.status === activeTab;
      const searchMatch  = getOrderId(o).toLowerCase().includes(search.toLowerCase()) || getName(o).toLowerCase().includes(search.toLowerCase());
      const statusMatch  = filterStatus  === 'All' || o.status          === filterStatus;
      const paymentMatch = filterPayment === 'All' || o.paymentMethod   === filterPayment;
      const orderDate    = new Date(getDate(o));
      const fromMatch    = dateFrom ? orderDate >= new Date(dateFrom) : true;
      const toMatch      = dateTo   ? orderDate <= new Date(dateTo)   : true;
      return tabMatch && searchMatch && statusMatch && paymentMatch && fromMatch && toMatch;
    })
    .sort((a, b) => {
      if (sortBy === 'date_desc') return new Date(getDate(b)) - new Date(getDate(a));
      if (sortBy === 'date_asc')  return new Date(getDate(a)) - new Date(getDate(b));
      if (sortBy === 'amount')    return Number(getAmount(b)) - Number(getAmount(a));
      if (sortBy === 'name')      return getName(a).localeCompare(getName(b));
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filteredOrders.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const pageWindow = () => {
    const pages = [];
    const start = Math.max(1, safePage - 1);
    const end   = Math.min(totalPages, start + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const activeFilterCount = [
    filterStatus  !== 'All',
    filterPayment !== 'All',
    dateFrom !== null,
    dateTo   !== null,
  ].filter(Boolean).length;

  if (view === 'detail' && selectedOrder) {
    return (
      <>
        {showAlert && <SweetAlert message={alertMessage} type={alertType} />}
        <OrderDetailView
          order={selectedOrder}
          onBack={() => { setView('list'); setSelectedOrder(null); }}
          onStatusChange={handleStatusChange}
          updatingId={updatingId}
        />
      </>
    );
  }

  if (view === 'chat' && selectedOrder) {
    return (
      <>
        {showAlert && <SweetAlert message={alertMessage} type={alertType} />}
        <OrderChatView
          baseUrl={baseUrl}
          baseUrlForFiles={baseUrlForFiles}
          order={selectedOrder}
          onBack={() => { setView('list'); setSelectedOrder(null); }}
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="OP-loading-screen">
        <div className="OP-spinner" />
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="OP-container">
      {showAlert && <SweetAlert message={alertMessage} type={alertType} />}

      {showDateModal && (
        <DateFilterModal
          onClose={() => setShowDateModal(false)}
          onApply={(from, to) => {
            setDateFrom(from);
            setDateTo(to);
            showSweetAlert(
              from || to ? 'Date filter applied!' : 'Date filter cleared!',
              from || to ? 'success' : 'warning'
            );
          }}
        />
      )}

      {showFiltersModal && (
        <FiltersModal
          currentStatus={filterStatus}
          currentPayment={filterPayment}
          onClose={() => setShowFiltersModal(false)}
          onApply={(s, p) => {
            setFilterStatus(s);
            setFilterPayment(p);
            showSweetAlert('Filters applied!');
          }}
        />
      )}

      <div className="OP-header">
        <div>
          <h1 className="OP-title">
            {activeTab === 'All Order' ? 'All Orders' : activeTab === 'Summary' ? 'Order Summary' : `${activeTab} Orders`}
          </h1>
          <p className="OP-subtitle">Manage and track all customer orders in one place.</p>
        </div>
        <button className="OP-filter-date-btn" onClick={() => setShowDateModal(true)}>
          <Calendar size={16} /> Filter Date
          {(dateFrom || dateTo) && <span className="OP-control-badge">!</span>}
        </button>
      </div>

      <div className="OP-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`OP-tab ${activeTab === tab ? 'OP-tab-active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Summary' ? (
        <SummaryView orders={orders} />
      ) : (
        <>
          <div className="OP-controls">
            <div className="OP-search-filter-wrapper">
              <div className="OP-search-box">
                <Search size={18} className="OP-search-icon" />
                <input
                  type="text"
                  placeholder="Search by Order ID or Customer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="OP-search-input"
                />
                {search && (
                  <button className="OP-search-clear" onClick={() => setSearch('')}><X size={14} /></button>
                )}
              </div>

              <button className="OP-control-btn" onClick={() => { setShowFiltersModal(true); setShowSortMenu(false); }}>
                <Filter size={16} /> Filter
                {activeFilterCount > 0 && <span className="OP-control-badge">{activeFilterCount}</span>}
              </button>

              <div className="OP-dropdown-wrapper">
                <button className="OP-control-btn" onClick={() => setShowSortMenu((p) => !p)}>
                  <ArrowUpDown size={16} /> Sort
                </button>
                {showSortMenu && (
                  <div className="OP-dropdown-menu">
                    {[
                      ['date_desc', 'Newest First'],
                      ['date_asc',  'Oldest First'],
                      ['amount',    'Highest Amount'],
                      ['name',      'Customer Name'],
                    ].map(([val, label]) => (
                      <div
                        key={val}
                        className={`OP-dropdown-item ${sortBy === val ? 'OP-dropdown-item-active' : ''}`}
                        onClick={() => { setSortBy(val); setShowSortMenu(false); }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {filteredOrders.length === 0 && (
            <div className="OP-empty-state">
              <p className="OP-empty-title">No orders found.</p>
              <p className="OP-empty-sub">Try adjusting your search or filters.</p>
            </div>
          )}

          {filteredOrders.length > 0 && (
            <div className="OP-table-card">
              <table className="OP-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Customer Name</th>
                    <th>Items</th>
                    <th>Payment Method</th>
                    <th>Total Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((order) => (
                    <tr key={order.id}>
                      <td><span className="OP-order-id">{getOrderId(order)}</span></td>
                      <td><span className="OP-date-cell">{getDate(order)}</span></td>
                      <td><span className="OP-customer-cell">{getName(order)}</span></td>
                      <td><span className="OP-items-cell">{getItems(order)} Item{getItems(order) > 1 ? 's' : ''}</span></td>
                      <td><span className="OP-payment-cell">{getPayment(order)}</span></td>
                      <td><span className="OP-amount-cell">{fmtAmount(getAmount(order))}</span></td>
                      <td>
                        <StatusSelect
                          order={order}
                          updating={updatingId === order.id}
                          onChange={handleStatusChange}
                        />
                      </td>
                      <td>
                        <RowActions
                          order={order}
                          onView={(o)    => { setSelectedOrder(o); setView('detail'); }}
                          onMessage={(o) => { setSelectedOrder(o); setView('chat'); }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="OP-table-footer">
                <span className="OP-count">
                  Showing {filteredOrders.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1} to{' '}
                  {Math.min(safePage * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length} orders
                </span>
                <div className="OP-pagination">
                  <button disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeft size={15} />
                  </button>
                  {pageWindow().map((p) => (
                    <button
                      key={p}
                      className={safePage === p ? 'OP-page-active' : ''}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  {totalPages > 3 && safePage < totalPages && (
                    <button
                      className={safePage === totalPages ? 'OP-page-active' : ''}
                      onClick={() => setPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  )}
                  <button disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrdersPage;